import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, EmptyState, Skeleton } from '../../design-system';
import { commandHistory } from '../../lib/command';
import './CommandBar.css';

// What was typed into the Command tab, and what each command did.
//
// ⚠️ THE ROWS WERE NEVER THE PROBLEM. `CommandLog` has stored the raw input,
// the parse and `executedAction` since the feature shipped — the screen's
// "Just now" list was a React state array, so every confirmed command vanished
// on the next refresh while the record of it sat in the database unread. This
// is the missing read, not a new log.
//
// ⚠️ IT SHOWS COMMANDS THAT DID NOTHING, TOO, and that is most of the value: a
// line typed, previewed and then abandoned is exactly the row that answers "I
// asked for that — why is it not there?". `executedAt` is what separates the
// two, and the row says which it is rather than leaving them to look alike.

const INTENT_LABEL = {
  create_task: 'Create task',
  clone_task: 'Clone task',
  apply_template: 'Apply template',
  update_task_status: 'Change status',
  reassign_task: 'Reassign',
  search_tasks: 'Find tasks',
  needs_clarification: 'Not understood',
};

const TIER_LABEL = { chips: 'fields', parser: 'parser', llm: 'AI' };

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? `today ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

/**
 * One line saying what the command actually did.
 *
 * ⚠️ READ OFF `executedAction`, WHICH IS WHAT `executeIntent` RETURNED — not
 * re-derived from the intent. The two differ: a sentence that named hours with
 * no preparer creates a task and budgets nothing, and only the stored result
 * knows that.
 */
function Outcome({ action, onOpen }) {
  if (!action) return null;

  if (action.kind === 'task_created') {
    return (
      <span className="cmd-hist-outcome">
        <button type="button" className="cmd-hist-link" onClick={() => onOpen(action.taskId)}>
          {action.ref} — {action.title}
        </button>
        {action.applied?.length ? <span className="cmd-done-detail">also set {action.applied.join(', ')}</span> : null}
        {action.note ? <span className="cmd-done-detail">{action.note}</span> : null}
      </span>
    );
  }
  if (action.kind === 'status_changed') {
    return <span className="cmd-hist-outcome">{action.ref} → {action.status}</span>;
  }
  if (action.kind === 'reassigned') {
    return <span className="cmd-hist-outcome">{action.ref} reassigned</span>;
  }
  if (action.kind === 'cloned' || action.kind === 'template_applied') {
    return (
      <span className="cmd-hist-outcome">
        {action.succeeded?.length || 0} task(s) created
        {action.failed?.length ? `, ${action.failed.length} skipped` : ''}
      </span>
    );
  }
  if (action.kind === 'search') {
    return <span className="cmd-hist-outcome">{action.count} task(s) found</span>;
  }
  return <span className="cmd-hist-outcome">{action.kind}</span>;
}

/**
 * @param {object[]} justNow  Results confirmed in THIS session, newest first.
 *   Prepended so a command that has just run appears immediately rather than
 *   after the next fetch — the server row exists either way, and the list is
 *   re-read on the next load.
 */
export default function CommandHistory({ justNow = [] }) {
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [executedOnly, setExecutedOnly] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async (only) => {
    setLoading(true);
    setError('');
    try {
      const data = await commandHistory({ executedOnly: only });
      setRows(data.commands || []);
      setHasMore(!!data.hasMore);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load your command history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(executedOnly); }, [load, executedOnly]);

  // Re-read when a command completes in this session, so the row that has just
  // been written replaces the optimistic one with the server's own copy.
  useEffect(() => { if (justNow.length) load(executedOnly); }, [justNow.length, load, executedOnly]);

  const loadOlder = async () => {
    if (!rows.length || more) return;
    setMore(true);
    try {
      const data = await commandHistory({ before: rows[rows.length - 1].id, executedOnly });
      setRows((r) => [...r, ...(data.commands || [])]);
      setHasMore(!!data.hasMore);
    } catch { /* what is on screen stays; the button can be pressed again */ } finally {
      setMore(false);
    }
  };

  const openTask = (id) => navigate(`/org-admin/tasks/${id}`);

  return (
    <div className="cmd-hist">
      <div className="cmd-hist-head">
        <label className="cmd-hist-filter">
          <input
            type="checkbox"
            checked={executedOnly}
            onChange={(e) => setExecutedOnly(e.target.checked)}
          />
          {' '}Only commands that ran
        </label>
        <Button variant="ghost" onClick={() => load(executedOnly)}>Refresh</Button>
      </div>

      {error && <p className="cmd-error">{error}</p>}
      {loading && <Skeleton.Text lines={4} />}

      {!loading && !rows.length && !error && (
        <EmptyState
          title="Nothing yet"
          description="Commands you type on this screen are kept here — what you asked for, and what it did."
        />
      )}

      {!loading && !!rows.length && (
        <ul className="cmd-hist-list">
          {rows.map((r) => (
            <li key={r.id} className={`cmd-hist-row${r.executedAt ? ' is-done' : ''}`}>
              <div className="cmd-hist-line">
                <span className="cmd-hist-text">“{r.text}”</span>
                <span className="cmd-hist-when">{when(r.at)}</span>
              </div>
              <div className="cmd-hist-meta">
                <span className="cmd-hist-tag">{INTENT_LABEL[r.intent] || r.intent}</span>
                <span className="cmd-hist-tag is-quiet">read by {TIER_LABEL[r.resolvedTier] || r.resolvedTier}</span>
                {/* An edited slot is a resolution the system got wrong while
                    believing it was right — worth showing, not just counting. */}
                {r.edited?.length ? (
                  <span className="cmd-hist-tag is-quiet">you corrected {r.edited.join(', ')}</span>
                ) : null}
              </div>
              {r.executedAt
                ? <Outcome action={r.action} onOpen={openTask} />
                : (
                  <span className="cmd-hist-outcome is-none">
                    Not run — this was previewed and never confirmed.
                  </span>
                )}
            </li>
          ))}
        </ul>
      )}

      {hasMore && !loading && (
        <Button variant="ghost" loading={more} onClick={loadOlder}>Load older</Button>
      )}
    </div>
  );
}
